const isAndroidWebview = (window: Window): boolean =>
  typeof window.Android?.refreshToken === "function";

const isIOSWebview = (window: Window): boolean =>
  typeof window.webkit?.messageHandlers?.refreshToken?.postMessage === "function";

export const isWebview = (window: Window): boolean =>
  isAndroidWebview(window) || isIOSWebview(window);

type RefreshTokenRequest = { request_id: string };
type RefreshTokenResponse = { request_id: string };

const isRefreshTokenResponse = (data: unknown): data is RefreshTokenResponse =>
  typeof data === "object" &&
  data !== null &&
  "request_id" in data &&
  typeof (data as RefreshTokenResponse).request_id === "string";

const listenForResponse = (requestId: string): Promise<void> =>
  new Promise<void>((resolve) => {
    const handleResponseMessage = (event: MessageEvent) => {
      const { data } = event;

      if (!data || typeof data !== "string") {
        return;
      }

      try {
        const parsedData = JSON.parse(data);

        if (!isRefreshTokenResponse(parsedData) || parsedData.request_id !== requestId) {
          return;
        }

        window.removeEventListener("message", handleResponseMessage);
        resolve();
      } catch {
        // do nothing
      }
    };

    window.addEventListener("message", handleResponseMessage);
  });

export const refreshToken = (): Promise<void> => {
  const requestId = Math.random().toString(36).substring(2, 15);
  const request: RefreshTokenRequest = { request_id: requestId };

  if (isAndroidWebview(window)) {
    const responsePromise = listenForResponse(requestId);
    window.Android.refreshToken(JSON.stringify(request));

    return responsePromise;
  }

  if (isIOSWebview(window)) {
    return window.webkit.messageHandlers.refreshToken.postMessage(request) as unknown as Promise<void>;
  }

  return Promise.reject(new Error("[webview] refreshToken: Unsupported platform"));
};
